import { useQuery } from "@apollo/client"
import { FundingUpdated } from "@perp/sdk-curie"
import Big from "big.js"
import { useMemo, useState } from "react"
import { usePerpInitializedEffect } from "sdk-react/perpetualProtocol/usePerpInitializedEffect"
import { Handlers } from "sdk-react/tools/useHandlers"
import { FUNDING_UPDATEDS_BY_BASE_TOKEN_QUERY, POLLING_INTERVAL } from "../graphServer/graphQueries"
import { useMarket } from "./useMarket"

interface APIFundingUpdated {
    baseToken: string
    dailyFundingRate: string
}

interface IFundingUpdated {
    fundingUpdateds: APIFundingUpdated[]
}

type FundingUpdatedInitialState = {
    tickerSymbol: string
}

export function useFundingUpdated(initialState: FundingUpdatedInitialState) {
    const { getHandlers } = Handlers.useContainer()
    const { onError } = getHandlers()

    const { tickerSymbol } = initialState
    const { market } = useMarket(tickerSymbol)
    const { baseAddress } = market || {}

    const {
        data,
        loading: isLoading,
        error: queryError,
    } = useQuery<IFundingUpdated>(FUNDING_UPDATEDS_BY_BASE_TOKEN_QUERY, {
        variables: { baseToken: baseAddress },
        pollInterval: POLLING_INTERVAL,
    })

    if (queryError) {
        onError(queryError)
    }

    const [dailyFundingRateMaxAbs, setDailyFundingRateMax] = useState<Big>()
    usePerpInitializedEffect(({ clearingHouseConfig }) => {
        setDailyFundingRateMax(clearingHouseConfig.maxFundingRateAbs)
    }, [])

    const fundingUpdated = useMemo(() => {
        if (!data || !data.fundingUpdateds.length || !dailyFundingRateMaxAbs) {
            return
        }
        // NOTE: only grab the first matched data
        const fundingUpdated = data.fundingUpdateds[0]
        return new FundingUpdated({
            baseToken: fundingUpdated.baseToken,
            dailyFundingRate: new Big(fundingUpdated.dailyFundingRate),
            dailyFundingRateMaxAbs,
        })
    }, [data, dailyFundingRateMaxAbs])

    return {
        isLoading,
        dailyFundingRate: fundingUpdated?.dailyFundingRate,
        dailyFundingRateMaxAbs: fundingUpdated?.dailyFundingRateMaxAbs,
        dailyFundingRateCapped: fundingUpdated?.dailyFundingRateCapped,
    }
}
